import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Mail, Phone, CheckCircle2, Loader2, ChevronLeft } from 'lucide-react';
import { RecaptchaVerifier, PhoneAuthProvider, updatePhoneNumber } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import { auth } from '../services/firebaseConfig';
import { useUserData } from './UserDataContext';
import { VerifiedBadge } from './VerifiedBadge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';

interface VerificationScreenProps {
  onClose: () => void;
  /** If set, auto-opens that tab */
  initialTab?: 'email' | 'phone';
}

type Step = 'overview' | 'email-enter' | 'email-code' | 'phone-enter' | 'phone-code';

const RESEND_COOLDOWN = 60;

// ISO 3166-1 alpha-2 (lowercase) → E.164 calling code.
// Covers every heritage country in this app + common diaspora targets.
const PHONE_CC: Record<string, string> = {
  ng: '+234', gh: '+233', za: '+27', ke: '+254', et: '+251', tz: '+255',
  eg: '+20',  ma: '+212', dz: '+213', ug: '+256', cm: '+237', sn: '+221',
  rw: '+250', tn: '+216', zw: '+263', bw: '+267', ci: '+225', zm: '+260',
  mz: '+258', ml: '+223', bf: '+226', ne: '+227', ao: '+244', so: '+252',
  td: '+235', sd: '+249', bj: '+229', tg: '+228', sl: '+232', lr: '+231',
  na: '+264', mu: '+230', mg: '+261', mw: '+265', ga: '+241', mr: '+222',
  gn: '+224', bi: '+257', er: '+291', gm: '+220', ls: '+266', sz: '+268',
  dj: '+253', cg: '+242', cd: '+243', cf: '+236', cv: '+238', km: '+269',
  gq: '+240', gw: '+245', st: '+239', ss: '+211', re: '+262',
  // Diaspora
  us: '+1', ca: '+1', gb: '+44', fr: '+33', de: '+49', br: '+55',
  // Custom flag codes used by AfroYap
  soulaan: '+1', rna: '+1', gg: '+1', ggm: '+1',
};

const detectCountryCallingCode = (profile: any): string => {
  const flag = (profile?.countryFlag || '').toLowerCase();
  if (flag && PHONE_CC[flag]) return PHONE_CC[flag];
  // Try mapping the full country name → ISO via a small fallback.
  const name = (profile?.country || '').toLowerCase();
  const nameToIso: Record<string, string> = {
    'nigeria': 'ng', 'ghana': 'gh', 'kenya': 'ke', 'south africa': 'za',
    'ethiopia': 'et', 'united states': 'us', 'united kingdom': 'gb',
    'canada': 'ca', 'france': 'fr', 'brazil': 'br',
  };
  const iso = nameToIso[name];
  if (iso && PHONE_CC[iso]) return PHONE_CC[iso];
  return '+1'; // sensible default
};

/**
 * Ask the native Firebase SDK to send the SMS and link the number, resolving
 * with the verificationId it reports back.
 *
 * The web flow needs a reCAPTCHA widget rendered into the page. Inside a
 * Capacitor WebView that widget is fighting the container it runs in: the
 * origin is not a domain Firebase can authorize, so verifyPhoneNumber either
 * throws auth/invalid-app-credential or hangs until the 45s timeout. The native
 * SDKs do their own app attestation (APNs silent push on iOS, Play Integrity on
 * Android) and need no widget at all.
 *
 * `linkWithPhoneNumber` resolves void; the id arrives on the `phoneCodeSent`
 * listener and failures on `phoneVerificationFailed`, so both are bridged into
 * one promise here, with the listeners always torn down on the way out.
 */
async function nativeSendPhoneCode(phoneNumber: string, timeoutMs = 60000): Promise<string> {
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let sentHandle: { remove: () => Promise<void> } | null = null;
    let failHandle: { remove: () => Promise<void> } | null = null;

    const cleanup = () => {
      clearTimeout(timer);
      void sentHandle?.remove();
      void failHandle?.remove();
    };
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const timer = setTimeout(
      () => finish(() => reject(new Error('The SMS request timed out. Check the number and try again.'))),
      timeoutMs,
    );

    void (async () => {
      try {
        sentHandle = await FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
          finish(() => resolve(event.verificationId));
        });
        failHandle = await FirebaseAuthentication.addListener('phoneVerificationFailed', (event: any) => {
          finish(() => reject(new Error(event?.message || 'Phone verification failed.')));
        });
        await FirebaseAuthentication.linkWithPhoneNumber({ phoneNumber });
      } catch (err) {
        finish(() => reject(err));
      }
    })();
  });
}

export function VerificationScreen({ onClose, initialTab }: VerificationScreenProps) {
  const { userProfile, updateProfile } = useUserData();
  const fns = getFunctions(getApp());
  const isNative = Capacitor.isNativePlatform();

  const [step, setStep] = useState<Step>(
    initialTab === 'email' ? 'email-enter' :
    initialTab === 'phone' ? 'phone-enter' :
    'overview'
  );
  const [emailInput, setEmailInput]   = useState(
    userProfile?.isRelayEmail ? '' : (userProfile?.email || '')
  );
  const [phoneInput, setPhoneInput]   = useState<string>(userProfile?.phone || '');
  const [codeInput, setCodeInput]     = useState('');
  const [loading, setLoading]         = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Firebase Phone Auth state
  const verificationIdRef    = useRef<string | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const timerRef             = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    recaptchaVerifierRef.current?.clear();
  }, []);

  const startResendTimer = () => {
    setResendTimer(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setResendTimer(t => {
        if (t <= 1) { clearInterval(timerRef.current!); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  // ── EMAIL ────────────────────────────────────────────────────────────────

  const sendEmailCode = async () => {
    if (!emailInput.trim()) return toast.error('Enter an email address.');
    setLoading(true);
    try {
      await httpsCallable(fns, 'sendEmailCode')({ email: emailInput.trim() });
      setStep('email-code');
      setCodeInput('');
      startResendTimer();
      toast.success('Code sent! Check your inbox.');
    } catch (e: any) {
      toast.error(e.message?.replace('Firebase: ', '') || 'Failed to send code.');
    } finally { setLoading(false); }
  };

  const verifyEmailCode = async () => {
    if (codeInput.length !== 6) return toast.error('Enter the 6-digit code.');
    setLoading(true);
    try {
      const result: any = await httpsCallable(fns, 'verifyEmailCode')({ code: codeInput });
      updateProfile({ emailVerified: true, email: emailInput.trim(), isRelayEmail: false, verifiedBadge: result.data.verifiedBadge } as any);
      toast.success('Email verified!');
      setStep('overview');
    } catch (e: any) {
      toast.error(e.message?.replace('Firebase: ', '') || 'Verification failed.');
    } finally { setLoading(false); }
  };

  // ── PHONE (Firebase Phone Auth) ──────────────────────────────────────────

  const sendPhoneCode = async () => {
    const ph = phoneInput.trim();
    console.log('[Verify] sendPhoneCode start, raw input:', ph);
    if (!ph) return toast.error('Enter a phone number.');

    // Normalize: strip spaces, parens, dashes so common formats survive.
    let normalized = ph.replace(/[\s()\-]/g, '');

    // Auto-prepend "+" if missing. The country code itself comes from the
    // leading digits of what the user actually typed — we don't infer it from
    // their heritage flag.
    if (!normalized.startsWith('+')) {
      // If they typed a leading "00" (international prefix used in many countries),
      // convert that to "+". Otherwise just prepend "+".
      if (normalized.startsWith('00')) normalized = '+' + normalized.slice(2);
      else normalized = '+' + normalized;
    }
    console.log('[Verify] normalized:', normalized);

    if (!/^\+[1-9]\d{6,14}$/.test(normalized)) {
      toast.error('Enter your full phone number including country code, e.g. 14155550123');
      return;
    }
    setLoading(true);
    try {
      if (isNative) {
        // Native SDK path — no reCAPTCHA widget, no authorized-domain problem.
        console.log('[Verify] Native phone link…');
        verificationIdRef.current = await nativeSendPhoneCode(normalized);
        console.log('[Verify] Native phoneCodeSent received.');
        setPhoneInput(normalized);
        setStep('phone-code');
        setCodeInput('');
        startResendTimer();
        toast.success('Code sent via SMS.');
        return;
      }

      // The recaptcha-container-phone div lives in the portal-rendered modal.
      // If it's missing for any reason, RecaptchaVerifier silently rejects.
      const container = document.getElementById('recaptcha-container-phone');
      if (!container) {
        toast.error('reCAPTCHA container missing. Reload and try again.');
        console.error('[Verify] recaptcha-container-phone not found in DOM');
        return;
      }

      // Reset any previous verifier before creating a new one
      try { recaptchaVerifierRef.current?.clear(); } catch { /* ignore */ }

      console.log('[Verify] Creating RecaptchaVerifier…');
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        'recaptcha-container-phone',
        { size: 'invisible' }
      );

      console.log('[Verify] Rendering reCAPTCHA…');
      await recaptchaVerifierRef.current.render();

      console.log('[Verify] Calling provider.verifyPhoneNumber()…');
      const provider = new PhoneAuthProvider(auth);
      // 45s timeout — if Firebase can't initialise reCAPTCHA Enterprise and v2
      // falls back silently, the promise can hang forever. Surface a clear error.
      verificationIdRef.current = await Promise.race([
        provider.verifyPhoneNumber(normalized, recaptchaVerifierRef.current),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('TIMEOUT: SMS request did not complete in 45s. Phone Auth may not be fully configured in Firebase Console (reCAPTCHA Enterprise / authorized domains).')), 45000)),
      ]);

      console.log('[Verify] verifyPhoneNumber resolved. Switching to code step.');
      setPhoneInput(normalized);
      setStep('phone-code');
      setCodeInput('');
      startResendTimer();
      toast.success('Code sent via SMS.');
    } catch (e: any) {
      console.error('[Verify] sendPhoneCode error:', e);
      try { recaptchaVerifierRef.current?.clear(); } catch { /* ignore */ }
      recaptchaVerifierRef.current = null;
      const code = e?.code || '';
      const msg = e?.message || '';
      // Common Firebase auth errors → human-readable messages
      let display = msg.replace('Firebase: ', '');
      if (code === 'auth/invalid-app-credential') display = 'reCAPTCHA verification failed. Refresh the page and try again.';
      else if (code === 'auth/captcha-check-failed') display = 'reCAPTCHA challenge failed. Try again.';
      else if (code === 'auth/invalid-phone-number') display = 'Phone number is invalid. Use E.164 format like +14155550123.';
      else if (code === 'auth/too-many-requests') display = 'Too many attempts. Wait a few minutes before retrying.';
      else if (code === 'auth/unauthorized-domain') display = 'This domain is not authorized for phone auth in Firebase settings.';
      else if (!display) display = 'Failed to send code. Check the browser console for details.';
      toast.error(display);
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (codeInput.length !== 6) return toast.error('Enter the 6-digit code.');
    if (!verificationIdRef.current) return toast.error('Session expired. Go back and try again.');
    setLoading(true);
    try {
      // Confirmed through the JS SDK on BOTH platforms. Native only sends the
      // SMS (that is the part reCAPTCHA breaks in a WebView); the verificationId
      // it returns is a backend session, usable from any SDK on this project.
      // Confirming natively instead linked to the NATIVE current user, and only
      // Google sign-ins have one: username/password accounts sign in through
      // the JS SDK alone, so the native link failed with no user signed in.
      if (!auth.currentUser) throw new Error('You are signed out. Sign in again and retry.');
      const credential = PhoneAuthProvider.credential(verificationIdRef.current, codeInput);
      await updatePhoneNumber(auth.currentUser, credential);
      // Force token refresh so the CF reads the updated Auth record.
      await auth.currentUser?.getIdToken(true);
      const result: any = await httpsCallable(fns, 'confirmPhoneVerified')({});
      updateProfile({ phoneVerified: true, phone: phoneInput.trim(), verifiedBadge: result.data.verifiedBadge } as any);
      toast.success('Phone verified!');
      setStep('overview');
    } catch (e: any) {
      toast.error(e.message?.replace('Firebase: ', '') || 'Verification failed.');
    } finally { setLoading(false); }
  };

  // ── Derived state ────────────────────────────────────────────────────────

  const emailVerified = userProfile?.emailVerified === true;
  const phoneVerified = userProfile?.phoneVerified === true;
  const badgeEarned   = userProfile?.verifiedBadge === true;
  const isRelayEmail  = userProfile?.isRelayEmail === true;

  const emailHint = isRelayEmail
    ? 'Apple is hiding your real email — verify a real one to earn your badge.'
    : emailVerified
    ? `Verified: ${userProfile?.email}`
    : 'Unverified — tap to verify.';

  const phoneHint = phoneVerified
    ? `Verified: ${userProfile?.phone}`
    : 'Unverified — tap to verify.';

  return createPortal(
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Invisible reCAPTCHA container — must stay in DOM whenever phone step is active */}
      <div id="recaptcha-container-phone" />

      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 32, stiffness: 300 }}
        className="relative z-10 w-full max-w-lg bg-[#0D0D0D] border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {step !== 'overview' && (
              <button
                onClick={() => { setStep('overview'); setCodeInput(''); }}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-bold text-white">
                {step === 'overview'    ? 'Verified Badge' :
                 step === 'email-enter' ? 'Verify Email'   :
                 step === 'email-code'  ? 'Enter Code'     :
                 step === 'phone-enter' ? 'Verify Phone'   :
                 /* phone-code */         'Enter Code'}
              </h2>
              <p className="text-xs text-white/40 mt-0.5">
                {step === 'overview'   ? (badgeEarned ? 'Both verified — badge earned!' : 'Verify both to earn your badge') :
                 step === 'email-code' ? `Code sent to ${emailInput}` :
                 step === 'phone-code' ? `Code sent to ${phoneInput}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="px-6 pt-5 space-y-4">
          <AnimatePresence mode="wait">

            {/* ── OVERVIEW ── */}
            {step === 'overview' && (
              <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">

                {badgeEarned && (
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#1D9BF0]/10 border border-[#1D9BF0]/30">
                    <VerifiedBadge size={28} />
                    <p className="text-sm text-white font-semibold">You've earned your verified badge!</p>
                  </div>
                )}

                {/* Email row */}
                <button
                  onClick={() => !emailVerified && setStep('email-enter')}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-colors text-left ${
                    emailVerified
                      ? 'bg-green-900/20 border-green-500/30 cursor-default'
                      : 'bg-white/5 border-white/10 hover:bg-white/8 cursor-pointer'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${emailVerified ? 'bg-green-500/20' : 'bg-white/10'}`}>
                    {emailVerified
                      ? <CheckCircle2 className="w-5 h-5 text-green-400" />
                      : <Mail className="w-5 h-5 text-white/60" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">Email</p>
                    <p className={`text-xs mt-0.5 truncate ${emailVerified ? 'text-green-400' : isRelayEmail ? 'text-yellow-400' : 'text-white/40'}`}>
                      {emailHint}
                    </p>
                  </div>
                  {!emailVerified && <span className="text-[#1D9BF0] text-sm font-medium shrink-0">Verify →</span>}
                </button>

                {/* Phone row */}
                <button
                  onClick={() => !phoneVerified && setStep('phone-enter')}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-colors text-left ${
                    phoneVerified
                      ? 'bg-green-900/20 border-green-500/30 cursor-default'
                      : 'bg-white/5 border-white/10 hover:bg-white/8 cursor-pointer'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${phoneVerified ? 'bg-green-500/20' : 'bg-white/10'}`}>
                    {phoneVerified
                      ? <CheckCircle2 className="w-5 h-5 text-green-400" />
                      : <Phone className="w-5 h-5 text-white/60" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">Phone</p>
                    <p className={`text-xs mt-0.5 truncate ${phoneVerified ? 'text-green-400' : 'text-white/40'}`}>
                      {phoneHint}
                    </p>
                  </div>
                  {!phoneVerified && <span className="text-[#1D9BF0] text-sm font-medium shrink-0">Verify →</span>}
                </button>

              </motion.div>
            )}

            {/* ── EMAIL ENTER ── */}
            {step === 'email-enter' && (
              <motion.div key="email-enter" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                {isRelayEmail && (
                  <p className="text-xs text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-xl px-4 py-3">
                    Apple is hiding your real email — enter a real email address to earn your verified badge.
                  </p>
                )}
                <div className="space-y-2">
                  <label className="text-xs text-white/50 font-medium uppercase tracking-wide">Email address</label>
                  <Input
                    type="email"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-white/8 border-white/10 text-white placeholder:text-white/30"
                    onKeyDown={e => e.key === 'Enter' && sendEmailCode()}
                  />
                </div>
                <Button
                  onClick={sendEmailCode}
                  disabled={loading || !emailInput.trim()}
                  className="w-full h-12 font-bold"
                  style={{ background: 'linear-gradient(135deg, #4478ea, #2d1ef1)' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Code'}
                </Button>
              </motion.div>
            )}

            {/* ── EMAIL CODE ── */}
            {step === 'email-code' && (
              <motion.div key="email-code" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <CodeInput value={codeInput} onChange={setCodeInput} onComplete={verifyEmailCode} />
                <Button
                  onClick={verifyEmailCode}
                  disabled={loading || codeInput.length !== 6}
                  className="w-full h-12 font-bold"
                  style={{ background: 'linear-gradient(135deg, #4478ea, #2d1ef1)' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                </Button>
                <ResendButton timer={resendTimer} onResend={sendEmailCode} loading={loading} />
              </motion.div>
            )}

            {/* ── PHONE ENTER ── */}
            {step === 'phone-enter' && (
              <motion.div key="phone-enter" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs text-white/50 font-medium uppercase tracking-wide">Phone number</label>
                  <Input
                    type="tel"
                    value={phoneInput}
                    onChange={e => setPhoneInput(e.target.value)}
                    placeholder="e.g. 14155550123 or +14155550123"
                    className="bg-white/8 border-white/10 text-white placeholder:text-white/30"
                    onKeyDown={e => e.key === 'Enter' && sendPhoneCode()}
                  />
                  <p className="text-xs text-white/30">Enter your full number including country code. The "+" is added automatically.</p>
                </div>
                <Button
                  onClick={sendPhoneCode}
                  disabled={loading || !phoneInput.trim()}
                  className="w-full h-12 font-bold"
                  style={{ background: 'linear-gradient(135deg, #4478ea, #2d1ef1)' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Code'}
                </Button>
              </motion.div>
            )}

            {/* ── PHONE CODE ── */}
            {step === 'phone-code' && (
              <motion.div key="phone-code" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <CodeInput value={codeInput} onChange={setCodeInput} onComplete={verifyPhoneCode} />
                <Button
                  onClick={verifyPhoneCode}
                  disabled={loading || codeInput.length !== 6}
                  className="w-full h-12 font-bold"
                  style={{ background: 'linear-gradient(135deg, #4478ea, #2d1ef1)' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                </Button>
                <ResendButton timer={resendTimer} onResend={sendPhoneCode} loading={loading} />
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

// ── 6-box code input ──────────────────────────────────────────────────────────
function CodeInput({ value, onChange, onComplete }: {
  value: string;
  onChange: (v: string) => void;
  onComplete: () => void;
}) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  const handleChange = (i: number, ch: string) => {
    const digits = ch.replace(/\D/g, '').slice(0, 1);
    const arr = value.split('');
    arr[i] = digits;
    const next = arr.join('').slice(0, 6);
    onChange(next);
    if (digits && i < 5) inputs.current[i + 1]?.focus();
    if (next.length === 6) setTimeout(onComplete, 100);
  };
  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) { onChange(pasted); setTimeout(onComplete, 100); }
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={el => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKey(i, e)}
          className="w-11 h-14 rounded-xl bg-white/8 border border-white/15 text-white text-center text-2xl font-bold focus:border-[#4478ea] focus:outline-none transition-colors"
        />
      ))}
    </div>
  );
}

// ── Resend button with countdown ─────────────────────────────────────────────
function ResendButton({ timer, onResend, loading }: { timer: number; onResend: () => void; loading: boolean }) {
  return (
    <p className="text-center text-xs text-white/40">
      Didn't receive it?{' '}
      {timer > 0
        ? <span className="text-white/30">Resend in {timer}s</span>
        : <button
            onClick={onResend}
            disabled={loading}
            className="text-[#4478ea] underline disabled:opacity-50"
          >Resend</button>
      }
    </p>
  );
}
